/**
 * Progressive enhancement for Search & Discovery filtering and sorting.
 *
 * The facet form is a plain GET form, so filtering works with scripts disabled
 * and every combination is a real URL. This file intercepts submission, fetches
 * the same URL through the Section Rendering API, and swaps the grid in place —
 * keeping scroll position and the mobile drawer open.
 *
 * Both copies of the form (sidebar and drawer) post to the same place, so
 * whichever the customer used, the result is identical.
 */

const GRID = '[data-section-id]';

function grid() {
  return document.querySelector(GRID);
}

function sectionId() {
  return grid()?.dataset.sectionId;
}

/** Build the filtered URL from a form, dropping empty fields so URLs stay clean. */
function urlFrom(form) {
  const params = new URLSearchParams();

  for (const [key, value] of new FormData(form).entries()) {
    if (value !== '') params.append(key, value);
  }

  const query = params.toString();
  return `${form.getAttribute('action')}${query ? `?${query}` : ''}`;
}

/**
 * The filter request still on its way, if any. A customer ticking boxes
 * quickly starts a request per tick, and they can answer out of order; a slow
 * older one landing last would put back a grid and an address the customer
 * has already moved on from. Each new change cancels the one before it.
 */
let inFlight = null;

async function apply(url, { pushState = true } = {}) {
  const id = sectionId();
  const container = grid();
  if (!id || !container) return;

  inFlight?.abort();
  const request = new AbortController();
  inFlight = request;

  container.setAttribute('aria-busy', 'true');

  try {
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}section_id=${id}`, { signal: request.signal });
    if (!response.ok) throw new Error(response.statusText);

    const html = await response.text();
    // Checked again here as well as by the fetch: a body that finished
    // arriving just as a newer change started is still out of date.
    if (request.signal.aborted) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector(GRID);

    // The mobile drawer sits outside the swapped grid, so its form and result
    // count have to be refreshed separately or they drift out of sync. Swapping
    // the body rather than just the form keeps the "Show N products" button
    // honest.
    const drawerBody = document.getElementById('collection-filters-body');
    const incomingDrawerBody = parsed.getElementById('collection-filters-body');

    if (incoming) container.replaceWith(incoming);
    if (drawerBody && incomingDrawerBody) drawerBody.replaceWith(incomingDrawerBody);

    if (pushState) window.history.pushState({ url }, '', url);
  } catch {
    // Cancelled for a newer change, which now owns the grid and the address.
    if (request.signal.aborted) return;

    // Fall back to a normal navigation rather than leaving a stale grid.
    window.location.href = url;
  } finally {
    // Only the latest request clears the busy state: a cancelled one finishing
    // would otherwise mark the grid ready while its replacement still loads.
    if (inFlight === request) {
      inFlight = null;
      grid()?.removeAttribute('aria-busy');
    }
  }
}

/** Checkbox and price changes apply immediately. */
document.addEventListener('change', (event) => {
  const form = event.target.closest('[data-facet-form]');
  if (form) {
    apply(urlFrom(form));
    return;
  }

  const sort = event.target.closest('[data-sort-select]');
  if (sort) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    apply(url.toString());
  }
});

/** Debounce the price inputs so typing a value does not fire per keystroke. */
let priceTimer;
document.addEventListener('input', (event) => {
  const input = event.target.closest('.facet__price input');
  if (!input) return;

  clearTimeout(priceTimer);
  const form = input.closest('[data-facet-form]');
  priceTimer = setTimeout(() => apply(urlFrom(form)), 500);
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-facet-form]');
  if (!form) return;
  event.preventDefault();
  apply(urlFrom(form));
});

/** "Clear all", an "all products" row and pagination stay in place rather than reloading. */
document.addEventListener('click', (event) => {
  const link = event.target.closest('.facets__clear, .facet__option--all, .pagination__link');
  if (!link || !link.href) return;

  event.preventDefault();
  apply(link.href);

  if (link.classList.contains('pagination__link')) {
    grid()?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }
});

/** Keep the back button working after in-place swaps. */
window.addEventListener('popstate', () => {
  apply(window.location.href, { pushState: false });
});
